import * as THREE from 'three';

interface RadarBlip {
  position: THREE.Vector3;
  color: string;
  type: 'enemy' | 'target' | 'objective';
}

export class Radar {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private container: HTMLElement;

  private readonly SIZE = 150;
  private readonly RANGE = 80; // World units visible on radar
  private readonly CENTER: number;

  private playerPos: THREE.Vector3 = new THREE.Vector3();
  private playerRotation: number = 0;
  private blips: RadarBlip[] = [];

  constructor() {
    this.CENTER = this.SIZE / 2;

    // Create container
    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: absolute;
      top: 20px;
      left: 20px;
      width: ${this.SIZE}px;
      height: ${this.SIZE}px;
      border: 2px solid #00ffff;
      border-radius: 50%;
      box-shadow: 0 0 15px #00ffff, inset 0 0 20px rgba(0, 255, 255, 0.1);
      overflow: hidden;
    `;

    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.SIZE;
    this.canvas.height = this.SIZE;
    this.canvas.style.cssText = `
      width: 100%;
      height: 100%;
    `;

    this.container.appendChild(this.canvas);
    document.getElementById('ui-container')?.appendChild(this.container);

    this.ctx = this.canvas.getContext('2d')!;
  }

  public setPlayerPosition(pos: THREE.Vector3, rotation: number): void {
    this.playerPos.copy(pos);
    this.playerRotation = rotation;
  }

  public clearBlips(): void {
    this.blips = [];
  }

  public addBlip(position: THREE.Vector3, type: 'enemy' | 'target' | 'objective'): void {
    const colors = {
      enemy: '#ff0000',
      target: '#ff00ff',
      objective: '#ffff00',
    };

    this.blips.push({
      position: position.clone(),
      color: colors[type],
      type,
    });
  }

  public render(): void {
    const ctx = this.ctx;

    // Clear
    ctx.clearRect(0, 0, this.SIZE, this.SIZE);

    // Background
    ctx.fillStyle = 'rgba(0, 10, 20, 0.8)';
    ctx.beginPath();
    ctx.arc(this.CENTER, this.CENTER, this.CENTER - 2, 0, Math.PI * 2);
    ctx.fill();

    // Grid circles
    ctx.strokeStyle = 'rgba(0, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    for (let r = 25; r < this.CENTER; r += 25) {
      ctx.beginPath();
      ctx.arc(this.CENTER, this.CENTER, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Grid lines
    ctx.beginPath();
    ctx.moveTo(this.CENTER, 0);
    ctx.lineTo(this.CENTER, this.SIZE);
    ctx.moveTo(0, this.CENTER);
    ctx.lineTo(this.SIZE, this.CENTER);
    ctx.stroke();

    // Sweep effect
    const sweepAngle = (performance.now() / 1000) * 2;
    const gradient = typeof ctx.createConicGradient === 'function'
      ? ctx.createConicGradient(sweepAngle, this.CENTER, this.CENTER)
      : null;

    if (gradient) {
      gradient.addColorStop(0, 'rgba(0, 255, 255, 0.3)');
      gradient.addColorStop(0.1, 'rgba(0, 255, 255, 0)');
      gradient.addColorStop(1, 'rgba(0, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(this.CENTER, this.CENTER, this.CENTER - 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw blips
    for (const blip of this.blips) {
      this.drawBlip(blip);
    }

    // Player indicator (center)
    ctx.fillStyle = '#00ff00';
    ctx.shadowColor = '#00ff00';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(this.CENTER, this.CENTER - 6);
    ctx.lineTo(this.CENTER - 4, this.CENTER + 4);
    ctx.lineTo(this.CENTER + 4, this.CENTER + 4);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    // Direction indicator
    const dirX = Math.sin(this.playerRotation) * 8;
    const dirY = -Math.cos(this.playerRotation) * 8;
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(this.CENTER, this.CENTER);
    ctx.lineTo(this.CENTER + dirX, this.CENTER + dirY);
    ctx.stroke();
  }

  private drawBlip(blip: RadarBlip): void {
    // Calculate relative position
    const dx = blip.position.x - this.playerPos.x;
    const dz = blip.position.z - this.playerPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Skip if out of range
    if (distance > this.RANGE) return;

    // Convert to radar coordinates (rotate by player facing)
    const angle = Math.atan2(dx, dz) - this.playerRotation;
    const radarDist = (distance / this.RANGE) * (this.CENTER - 10);

    const x = this.CENTER + Math.sin(angle) * radarDist;
    const y = this.CENTER - Math.cos(angle) * radarDist;

    // Draw blip
    this.ctx.fillStyle = blip.color;
    this.ctx.shadowColor = blip.color;
    this.ctx.shadowBlur = 8;

    if (blip.type === 'enemy') {
      // Diamond for enemies
      this.ctx.beginPath();
      this.ctx.moveTo(x, y - 4);
      this.ctx.lineTo(x + 4, y);
      this.ctx.lineTo(x, y + 4);
      this.ctx.lineTo(x - 4, y);
      this.ctx.closePath();
      this.ctx.fill();
    } else if (blip.type === 'target') {
      // Square for targets
      this.ctx.fillRect(x - 3, y - 3, 6, 6);
    } else {
      // Circle for objectives
      this.ctx.beginPath();
      this.ctx.arc(x, y, 4, 0, Math.PI * 2);
      this.ctx.fill();
    }

    this.ctx.shadowBlur = 0;
  }

  public destroy(): void {
    this.container.remove();
  }
}
